/**
 * TPC UI primitives.
 *
 * Deliberately dependency-free: React plus `Link` from react-router. Every
 * piece reads the tokens in `tokens.css` and knows nothing about the portal,
 * Redux or MUI, so the whole folder can move into a private package unchanged.
 */
import {
  forwardRef,
  useId,
  useState,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Link } from "react-router";

import type { StatusTone, TagTone, Tone } from "./tokens";

const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(" ");

export type { StatusTone, TagTone, Tone };

/** Semantic type classes, so screens can compose without repeating names. */
export const TYPE = {
  eyebrow: "ui-eyebrow",
  section: "ui-section-label",
  title: "ui-title",
  lead: "ui-lead",
  body: "ui-body",
  small: "ui-small",
  caption: "ui-caption",
  number: "ui-num",
} as const;

/** ───────────────────────────────────────────────────────── page structure */

export interface Crumb {
  label: string;
  to?: string;
}

export function Breadcrumb({ items }: { items: Array<Crumb | [string, string?]> }) {
  const crumbs: Crumb[] = items.map((item) =>
    Array.isArray(item) ? { label: item[0], to: item[1] } : item,
  );
  return (
    <nav aria-label="Breadcrumb" className="ui-crumbs">
      {crumbs.map((crumb, index) => (
        <span key={`${crumb.label}-${index}`} className="ui-row" style={{ gap: 4 }}>
          {index > 0 && (
            <span className="ui-crumbs__sep" aria-hidden>
              ›
            </span>
          )}
          {crumb.to ? <Link to={crumb.to}>{crumb.label}</Link> : <span>{crumb.label}</span>}
        </span>
      ))}
    </nav>
  );
}

export function OwnerLine({
  owner,
  updated,
  contact,
}: {
  owner: string;
  updated?: string;
  contact?: string;
}) {
  return (
    <p className="ui-caption">
      Owner: {owner}
      {updated ? ` · Updated ${updated}` : ""}
      {contact ? (
        <>
          {" · "}
          <a href={contact} className="ui-link">
            Ask {owner}
          </a>
        </>
      ) : null}
    </p>
  );
}

export interface PageHeaderProps {
  eyebrow?: ReactNode;
  title?: ReactNode;
  summary?: ReactNode;
  crumbs?: Array<Crumb | [string, string?]>;
  actions?: ReactNode;
  meta?: ReactNode;
  className?: string;
}

/** Header for a page rendered inside the app shell. */
export function PageHeader({
  eyebrow,
  title,
  summary,
  crumbs,
  actions,
  meta,
  className,
}: PageHeaderProps) {
  return (
    <header className={cx("ui-pagehead", className)}>
      <div className="ui-pagehead__main">
        {crumbs && <Breadcrumb items={crumbs} />}
        {eyebrow && <div className="ui-eyebrow">{eyebrow}</div>}
        {title && <h1 className="ui-title ui-title--lg">{title}</h1>}
        {summary && <p className="ui-lead ui-pagehead__summary">{summary}</p>}
        {meta && <div className="ui-pagehead__meta">{meta}</div>}
      </div>
      {actions && <div className="ui-pagehead__actions">{actions}</div>}
    </header>
  );
}

/** A whole page outside the app shell (auth, payment form). */
export function PageShell({
  eyebrow,
  title,
  crumbs,
  summary,
  meta,
  actions,
  width = "default",
  children,
  className,
}: PageHeaderProps & {
  width?: "narrow" | "default" | "wide" | "full";
  children?: ReactNode;
}) {
  return (
    <div className={cx("ui-page", className)}>
      <div className={cx("ui-shell", `ui-shell--${width}`)}>
        {(eyebrow || title || summary || crumbs || actions || meta) && (
          <PageHeader
            eyebrow={eyebrow}
            title={title}
            crumbs={crumbs}
            summary={summary}
            meta={meta}
            actions={actions}
          />
        )}
        {children}
      </div>
    </div>
  );
}

export function Section({
  title,
  hint,
  right,
  id,
  className,
  children,
}: {
  title?: ReactNode;
  hint?: ReactNode;
  right?: ReactNode;
  id?: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <section id={id} className={cx("ui-section", className)}>
      {(title || right) && (
        <div className="ui-section__head">
          <div className="ui-section__titles">
            {title && <h2 className="ui-section-label">{title}</h2>}
            {hint && <p className="ui-section__hint">{hint}</p>}
          </div>
          <div className="ui-section__rule" />
          {right && <div className="ui-section__actions">{right}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Heading({
  eyebrow,
  children,
  id,
}: {
  eyebrow?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  return (
    <div id={id}>
      {eyebrow && <div className="ui-eyebrow">{eyebrow}</div>}
      <h2 className="ui-title">{children}</h2>
    </div>
  );
}

export function Toolbar({
  title,
  children,
  actions,
  className,
}: {
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("ui-toolbar", className)}>
      {title && <span className="ui-toolbar__title">{title}</span>}
      <div className="ui-toolbar__group">{children}</div>
      {actions && (
        <>
          <span className="ui-toolbar__spacer" />
          <div className="ui-toolbar__group">{actions}</div>
        </>
      )}
    </div>
  );
}

/** ────────────────────────────────────────────────────────────── surfaces */

export function Card({
  to,
  href,
  title,
  eyebrow,
  footer,
  children,
  className,
  padded = true,
}: {
  to?: string;
  href?: string;
  title?: ReactNode;
  eyebrow?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  const body = (
    <>
      {eyebrow && <div className={cx("ui-card__eyebrow ui-eyebrow")}>{eyebrow}</div>}
      {title && <div className="ui-card__title">{title}</div>}
      {children && <div className={cx("ui-card__body", !title && "ui-mt-0")}>{children}</div>}
      {footer && <div className="ui-card__footer">{footer}</div>}
    </>
  );
  const classes = cx(
    "ui-card",
    !padded && "ui-card--plain",
    (to || href) && "ui-card--link",
    className,
  );
  if (to) {
    return (
      <Link to={to} className={classes}>
        {body}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} target="_blank" rel="noopener noreferrer">
        {body}
      </a>
    );
  }
  return <div className={classes}>{body}</div>;
}

export function Grid({
  cols = 2,
  children,
  className,
}: {
  cols?: 1 | 2 | 3 | 4 | 6;
  children?: ReactNode;
  className?: string;
}) {
  return <div className={cx("ui-grid", `ui-grid--${cols}`, className)}>{children}</div>;
}

export function Callout({
  tone = "info",
  title,
  children,
  className,
}: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx("ui-callout", tone !== "info" && `ui-callout--${tone}`, className)}
      role={tone === "error" ? "alert" : undefined}
    >
      {title && <div className="ui-callout__title">{title}</div>}
      {children}
    </div>
  );
}

export function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="ui-steps">
      {items.map((item, index) => (
        <li key={index} className="ui-step">
          <span className="ui-step__n">{index + 1}</span>
          <span className="ui-step__text">{item}</span>
        </li>
      ))}
    </ol>
  );
}

export function BulletList({ items }: { items: ReactNode[] }) {
  return (
    <ul className="ui-bullets">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

export interface AccordionItem {
  id: string;
  title: ReactNode;
  content: ReactNode;
}

export function Accordion({
  items,
  openId,
  onToggle,
}: {
  items: AccordionItem[];
  openId?: string | null;
  onToggle?: (id: string) => void;
}) {
  const [own, setOwn] = useState<string | null>(null);
  const open = onToggle ? openId : own;
  const toggle = onToggle ?? ((id: string) => setOwn((current) => (current === id ? null : id)));
  return (
    <div className="ui-accordion">
      {items.map((item) => {
        const isOpen = open === item.id;
        return (
          <div key={item.id} className="ui-accordion__item" id={item.id}>
            <button
              type="button"
              className="ui-accordion__button"
              onClick={() => toggle(item.id)}
              aria-expanded={isOpen}
            >
              <span>{item.title}</span>
              <span
                className={cx("ui-accordion__chev", isOpen && "ui-accordion__chev--open")}
                aria-hidden
              >
                ▾
              </span>
            </button>
            {isOpen && <div className="ui-accordion__panel">{item.content}</div>}
          </div>
        );
      })}
    </div>
  );
}

/** ──────────────────────────────────────────────────────────────── data */

export function Tag({
  tone = "neutral",
  children,
  className,
}: {
  tone?: TagTone;
  children: ReactNode;
  className?: string;
}) {
  return <span className={cx("ui-tag", `ui-tag--${tone}`, className)}>{children}</span>;
}

export function YesNo({ value }: { value: ReactNode }) {
  const text = String(value ?? "").replace(/<[^>]+>/g, "").trim();
  if (/^yes/i.test(text)) return <Tag tone="brand">{text}</Tag>;
  if (/^no/i.test(text)) return <Tag tone="neutral">{text}</Tag>;
  return <>{text}</>;
}

export function FieldTable({
  rows,
  yesNoKey = "Billable",
}: {
  rows: Array<[ReactNode, ReactNode]>;
  yesNoKey?: string;
}) {
  return (
    <table className="ui-kv">
      <tbody>
        {rows.map(([key, value], index) => (
          <tr key={index}>
            <td className="ui-kv__k">{key}</td>
            <td className="ui-kv__v">
              {String(key) === yesNoKey ? <YesNo value={value} /> : value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function KeyValue({ rows }: { rows: Array<[ReactNode, ReactNode]> }) {
  return <FieldTable rows={rows} yesNoKey="" />;
}

export function DataTable({
  head,
  rows,
  align = [],
  empty = "No data available",
  className,
}: {
  head: ReactNode[];
  rows: ReactNode[][];
  align?: Array<"left" | "right" | undefined>;
  empty?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("ui-table-wrap", className)}>
      <table className="ui-table">
        <thead>
          <tr>
            {head.map((cell, index) => (
              <th key={index} className={align[index] === "right" ? "ui-right" : undefined}>
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="ui-table__empty" colSpan={Math.max(head.length, 1)}>
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={cx(align[cellIndex] === "right" && "ui-right ui-num")}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function StatusDot({ status }: { status?: StatusTone }) {
  return status ? (
    <span className={`ui-dot ui-dot--${status}`} aria-label={status} role="img" />
  ) : (
    <span className="ui-dot" style={{ background: "transparent" }} />
  );
}

export function KpiCard({
  label,
  value,
  sub,
  status,
  to,
  hint,
}: {
  label: ReactNode;
  value?: ReactNode;
  sub?: ReactNode;
  status?: StatusTone;
  to?: string;
  hint?: string;
}) {
  const body = (
    <>
      <div className="ui-kpi__label" title={hint}>
        <StatusDot status={status} />
        {label}
      </div>
      <div className="ui-kpi__value">
        {value ?? <Skeleton style={{ width: 64, height: 24 }} />}
      </div>
      {sub && (
        <div className={cx("ui-kpi__sub", status && `ui-kpi__sub--${status}`)}>{sub}</div>
      )}
    </>
  );
  return to ? (
    <Link to={to} className="ui-kpi">
      {body}
    </Link>
  ) : (
    <div className="ui-kpi">{body}</div>
  );
}

/** ────────────────────────────────────────────────────────────── controls */

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ComponentPropsWithoutRef<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  to?: string;
  href?: string;
}

export function Button({
  variant = "primary",
  size = "md",
  block,
  to,
  href,
  className,
  children,
  ...rest
}: ButtonProps) {
  const classes = cx(
    "ui-btn",
    `ui-btn--${variant}`,
    size !== "md" && `ui-btn--${size}`,
    block && "ui-btn--block",
    className,
  );
  if (to) {
    return (
      <Link to={to} className={classes}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={classes} {...rest}>
      {children}
    </button>
  );
}

export type Option = [value: string, label: string];

export function ChipGroup({
  value,
  options,
  onChange,
  size = "md",
}: {
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  size?: "sm" | "md";
}) {
  return (
    <div className="ui-chips" role="radiogroup">
      {options.map(([optionValue, label]) => (
        <button
          key={optionValue}
          type="button"
          role="radio"
          aria-checked={value === optionValue}
          onClick={() => onChange(optionValue)}
          className={cx("ui-chip", size === "sm" && "ui-chip--sm", value === optionValue && "ui-chip--active")}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function ChipMultiGroup({
  values,
  options,
  onChange,
  size = "md",
}: {
  values: string[];
  options: Option[];
  onChange: (values: string[]) => void;
  size?: "sm" | "md";
}) {
  const toggle = (value: string) =>
    onChange(
      values.includes(value)
        ? values.filter((current) => current !== value)
        : [...values, value],
    );
  return (
    <div className="ui-chips" role="group">
      {options.map(([optionValue, label]) => (
        <button
          key={optionValue}
          type="button"
          aria-pressed={values.includes(optionValue)}
          onClick={() => toggle(optionValue)}
          className={cx(
            "ui-chip",
            size === "sm" && "ui-chip--sm",
            values.includes(optionValue) && "ui-chip--active",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function Segmented({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Option[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="ui-segmented" role="tablist">
      {options.map(([optionValue, label]) => (
        <button
          key={optionValue}
          type="button"
          role="tab"
          aria-selected={value === optionValue}
          onClick={() => onChange(optionValue)}
          className={cx(
            "ui-segmented__item",
            value === optionValue && "ui-segmented__item--active",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
  className,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("ui-field", className)}>
      {label && (
        <label className="ui-field__label" htmlFor={htmlFor}>
          {label}
        </label>
      )}
      {children}
      {error ? (
        <span className="ui-field__error">{error}</span>
      ) : (
        hint && <span className="ui-field__hint">{hint}</span>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, ComponentPropsWithoutRef<"input">>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cx("ui-input", className)} {...rest} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  ComponentPropsWithoutRef<"textarea">
>(function Textarea({ className, rows = 4, ...rest }, ref) {
  return (
    <textarea ref={ref} rows={rows} className={cx("ui-textarea", className)} {...rest} />
  );
});

export const Select = forwardRef<HTMLSelectElement, ComponentPropsWithoutRef<"select">>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cx("ui-select", className)} {...rest}>
        {children}
      </select>
    );
  },
);

export const SearchInput = forwardRef<
  HTMLInputElement,
  ComponentPropsWithoutRef<"input"> & { size?: "sm" | "lg" }
>(function SearchInput({ className, size = "md", ...rest }, ref) {
  return (
    <div className="ui-search">
      <span className="ui-search__icon" aria-hidden>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <circle cx="11" cy="11" r="7" />
          <path d="m16.5 16.5 4 4" strokeLinecap="round" />
        </svg>
      </span>
      <input
        ref={ref}
        type="search"
        className={cx("ui-input", size === "lg" && "ui-input--lg", className)}
        {...rest}
      />
    </div>
  );
});

/** ──────────────────────────────────────────────────────────────── states */

export function Skeleton({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return <span className={cx("ui-skeleton", className)} style={style} aria-hidden />;
}

export function Loading({ children = "Loading…" }: { children?: ReactNode }) {
  return (
    <div className="ui-loading">
      <span className="ui-loading__spinner" />
      <span>{children}</span>
    </div>
  );
}

export function Empty({
  title = "Nothing here yet",
  children,
}: {
  title?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="ui-empty">
      <div className="ui-empty__title">{title}</div>
      {children && <div className="ui-empty__body">{children}</div>}
    </div>
  );
}

export function ErrorState({
  what = "this",
  message,
  onRetry,
}: {
  what?: string;
  message?: ReactNode;
  onRetry?: () => void;
}) {
  return (
    <Callout tone="error" title={`Couldn't load ${what}`}>
      {message}{" "}
      {onRetry && (
        <button type="button" className="ui-link" onClick={onRetry} style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}>
          Try again
        </button>
      )}
    </Callout>
  );
}

/** ──────────────────────────────────────────────────────────── app pieces */

export function Tabs({
  value,
  items,
  onChange,
  className,
}: {
  value: string;
  items: Array<{ value: string; label: ReactNode }>;
  onChange: (value: string) => void;
  className?: string;
}) {
  return (
    <div className={cx("ui-tabs", className)} role="tablist">
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          role="tab"
          aria-selected={value === item.value}
          className="ui-tab"
          onClick={() => onChange(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function Panel({
  title,
  subtitle,
  actions,
  footer,
  children,
  className,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx("ui-panel", className)}>
      {(title || actions) && (
        <div className="ui-panel__head">
          <div>
            {title && <div className="ui-panel__title">{title}</div>}
            {subtitle && <div className="ui-panel__subtitle">{subtitle}</div>}
          </div>
          {actions && <div className="ui-row">{actions}</div>}
        </div>
      )}
      {children && <div className="ui-panel__body">{children}</div>}
      {footer && <div className="ui-panel__foot">{footer}</div>}
    </section>
  );
}

export function Avatar({
  src,
  name,
  size = "md",
  className,
}: {
  src?: string;
  name?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const initials = (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");
  return (
    <span className={cx("ui-avatar", size !== "md" && `ui-avatar--${size}`, className)}>
      {src ? <img src={src} alt={name ?? ""} /> : initials || "—"}
    </span>
  );
}

export function Badge({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx("ui-badge", className)}>{children}</span>;
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  label,
}: {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  label?: ReactNode;
}) {
  const pages = buildPages(page, pageCount);
  return (
    <nav className="ui-pagination" aria-label="Pagination">
      {label && <span>{label}</span>}
      <div className="ui-pagination__pages">
        <button
          type="button"
          className="ui-page-btn"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          ‹
        </button>
        {pages.map((entry, index) =>
          entry === "…" ? (
            <span key={`gap-${index}`} className="ui-faint">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              className={cx("ui-page-btn", entry === page && "ui-page-btn--active")}
              aria-current={entry === page ? "page" : undefined}
              onClick={() => onPageChange(entry)}
            >
              {entry}
            </button>
          ),
        )}
        <button
          type="button"
          className="ui-page-btn"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
    </nav>
  );
}

function buildPages(page: number, pageCount: number): Array<number | "…"> {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }
  const pages: Array<number | "…"> = [1];
  if (page > 3) pages.push("…");
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);
  for (let current = start; current <= end; current += 1) pages.push(current);
  if (page < pageCount - 2) pages.push("…");
  pages.push(pageCount);
  return pages;
}

/** ─────────────────────────────────────────────────────────── utilities */

export function Html({
  html,
  as: As = "span",
  className,
}: {
  html: string;
  as?: "span" | "div" | "p";
  className?: string;
}) {
  return <As className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Stable ids for fields without threading useId through every screen. */
export function useFieldId(prefix = "field") {
  const id = useId();
  return `${prefix}-${id}`;
}
